import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { PdfTableColumn, PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";
import { measureTextPx } from "../textMeasure";
import { fitColumnWidths, longestWordPx, wrappedLines } from "../tablePdfLayout.mjs";
import {
  TABLE_CELL_PAD_V_PX,
  TABLE_BODY_LINE_HEIGHT_PX,
  TABLE_HEADER_PAD_V_PX,
  TABLE_HEADER_LINE_HEIGHT_PX,
  TABLE_FONT_SIZE_PX,
  TABLE_TITLE_HEIGHT_PX,
  TABLE_CATEGORY_COL_MAX_WIDTH_PX,
  TABLE_CATEGORY_NAME_MIN_WIDTH_PX,
  TABLE_CELL_PAD_H_PX,
  TABLE_AVG_ROW_BORDER_PX,
  TABLE_OUTER_PAD_FACTOR_PDF,
  NAME_GROUP_BYS,
} from "../../Utils/tableStyles";

// react-pdf gives every flex child flexShrink 1, so rows taller than the planned
// height would be squeezed and their text would run into the next row. Every row
// keeps its natural height instead, and the renderer only draws the rows that fit.
const s = StyleSheet.create({
  cellBordered: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 4,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  tableHeaderRow: { flexDirection: "row", flexShrink: 0, backgroundColor: "#fafafa", borderBottomWidth: 1, borderBottomColor: "#e8e8e8" },
  tableBodyRow: { flexDirection: "row", flexShrink: 0, borderBottomWidth: 0.5, borderBottomColor: "#f0f0f0" },
  // @react-pdf/renderer renders rgba() border alpha as opaque (dark). Use the solid
  // hex equivalent of the screen's rgba(0,0,0,0.15) flattened over white (#d9d9d9).
  tableAvgRow: { flexDirection: "row", flexShrink: 0, borderBottomWidth: TABLE_AVG_ROW_BORDER_PX, borderBottomColor: "#d9d9d9" },
  tablePosSepRow: { flexDirection: "row", flexShrink: 0, backgroundColor: "#f0f2f5", borderTopWidth: 1, borderTopColor: "#d0d5dd", borderBottomWidth: 0.5, borderBottomColor: "#f0f0f0" },
  tableTitle: { fontWeight: 600, color: "#1a1a1a", textAlign: "left" },
});

// Ant Design collapses the 1px row border-bottom, contributing ~0.5px per row.
const COLLAPSED_BORDER_PX = 0.5;
const HEADER_BORDER_PX = 1;
const POS_SEP_BORDER_PX = 1 + COLLAPSED_BORDER_PX;

type Row = Record<string, unknown>;

// Labels are measured at the heaviest weight a cell renders (the header and the
// colored values), so a width that fits the measure fits the PDF.
const measure = (text: string) => measureTextPx(text, `600 ${TABLE_FONT_SIZE_PX}px`);

function cellText(row: Row, col: PdfTableColumn, ci: number): string {
  if (row.__is_position_separator && ci > 0) return "";
  const key = col.dataIndex ?? col.key;
  // The label carries the metric's decimal count, the same as the live table.
  const label = row[`__label_${key}`];
  const val = row[key];
  return typeof label === "string" ? label : val != null ? String(val) : "";
}

function rowBorderPx(row: Row): number {
  if (row.__is_position_separator) return POS_SEP_BORDER_PX;
  return row.key === "avg" ? TABLE_AVG_ROW_BORDER_PX : COLLAPSED_BORDER_PX;
}

export function renderTable(
  snapshot: Extract<PdfWidgetSnapshot, { type: "table" }>,
  rect: PdfRect,
  scale: number,
) {
  const pad = Math.max(2, TABLE_OUTER_PAD_FACTOR_PDF * scale);
  const cols = snapshot.columns;
  const rows = snapshot.rows;
  const fontSize = TABLE_FONT_SIZE_PX * scale;
  const titleH = TABLE_TITLE_HEIGHT_PX * scale;
  const resolvedTitle = snapshot.title
    || (cols.length > 1 ? cols[1]?.title : null)
    || "Table";

  // Column widths in CSS px. A column is at least as wide as its widest word, the
  // way the screen's table breaks a line only at a space.
  const isNameGroupBy = NAME_GROUP_BYS.has(snapshot.groupBy ?? "");
  const minPx = cols.map((col, ci) => {
    const widestWord = Math.max(longestWordPx(col.title, measure), ...rows.map((row) => longestWordPx(cellText(row, col, ci), measure)));
    const floor = ci === 0 && isNameGroupBy ? TABLE_CATEGORY_NAME_MIN_WIDTH_PX : 0;
    return Math.max(floor, widestWord + 2 * TABLE_CELL_PAD_H_PX);
  });
  const colPx = fitColumnWidths(minPx, (rect.width - 2 * pad) / scale, TABLE_CATEGORY_COL_MAX_WIDTH_PX);
  const linesAt = (ci: number, text: string) => wrappedLines(text, colPx[ci] - 2 * TABLE_CELL_PAD_H_PX, measure);

  const headerLines = Math.max(...cols.map((col, ci) => linesAt(ci, col.title)));
  const headerH = (2 * TABLE_HEADER_PAD_V_PX + TABLE_HEADER_LINE_HEIGHT_PX * headerLines + HEADER_BORDER_PX) * scale;

  // Rows that fit, each as tall as its most-wrapped cell. The rest fall off the
  // bottom, the way the screen scrolls them away.
  let availH = rect.height - 2 * pad - titleH - headerH;
  const visibleRows: Row[] = [];
  for (const row of rows) {
    const lines = Math.max(...cols.map((col, ci) => linesAt(ci, cellText(row, col, ci))));
    const rowH = (2 * TABLE_CELL_PAD_V_PX + TABLE_BODY_LINE_HEIGHT_PX * lines + rowBorderPx(row)) * scale;
    if (rowH > availH) break;
    availH -= rowH;
    visibleRows.push(row);
  }

  const cellStyle = (ci: number, header: boolean, extra?: { fontWeight?: number; color?: string }) => [
    {
      width: colPx[ci] * scale,
      flexGrow: 0,
      flexShrink: 0,
      fontSize,
      // Unitless, so react-pdf multiplies it by the font size: the screen's line box.
      lineHeight: (header ? TABLE_HEADER_LINE_HEIGHT_PX : TABLE_BODY_LINE_HEIGHT_PX) / TABLE_FONT_SIZE_PX,
      color: "rgba(0,0,0,0.88)" as const,
      paddingVertical: (header ? TABLE_HEADER_PAD_V_PX : TABLE_CELL_PAD_V_PX) * scale,
      paddingHorizontal: TABLE_CELL_PAD_H_PX * scale,
      textAlign: "center" as const,
    },
    extra ?? {},
  ];

  return (
    <View style={[s.cellBordered, { left: rect.left, top: rect.top, width: rect.width, height: rect.height, padding: pad, flexDirection: "column" }]}>
      <View style={{ height: titleH, flexShrink: 0, flexDirection: "row", alignItems: "center", paddingHorizontal: 4 * scale }}>
        <Text style={[s.tableTitle, { fontSize: 10, flex: 1 }]}>
          {resolvedTitle}
        </Text>
        {snapshot.subtitle && (
          <Text style={{ fontSize: 7, color: "rgba(0,0,0,0.55)", textAlign: "right" }}>
            {snapshot.subtitle}
          </Text>
        )}
      </View>
      <View style={s.tableHeaderRow}>
        {cols.map((col, ci) => (
          <Text key={col.key} style={cellStyle(ci, true, { fontWeight: 600 })}>
            {col.title}
          </Text>
        ))}
      </View>
      {visibleRows.map((row, i) => {
        const isAvg = row.key === "avg";
        const isPosSep = !!row.__is_position_separator;
        const rowStyle = isPosSep ? s.tablePosSepRow : isAvg ? s.tableAvgRow : s.tableBodyRow;
        return (
          <View key={String(row.key ?? i)} style={rowStyle}>
            {cols.map((col, ci) => {
              const hex = row[`__color_${col.key}`] as string | undefined;
              return (
                <Text
                  key={col.key}
                  style={cellStyle(ci, false, {
                    ...(isPosSep && ci === 0 ? { fontWeight: 700 } : {}),
                    ...(isAvg && !isPosSep ? { fontWeight: 600 } : {}),
                    ...(hex && !isPosSep ? { color: hex, fontWeight: 600 } : {}),
                  })}
                >
                  {cellText(row, col, ci)}
                </Text>
              );
            })}
          </View>
        );
      })}
    </View>
  );
}
